import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import api from '../../services/apiService';
import './AdminFAQPage.css';

const AdminFAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [deletingFaq, setDeletingFaq] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'General',
    sortOrder: 0,
    isActive: true
  });
  const [errors, setErrors] = useState({});

  const fetchFaqs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/faq/admin/all');
      setFaqs(response.data);
    } catch (err) {
      console.error('Failed to fetch FAQs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFaqs();
  }, [fetchFaqs]);

  const openAddModal = () => {
    setEditingFaq(null);
    setFormData({ question: '', answer: '', category: 'General', sortOrder: 0, isActive: true });
    setErrors({});
    setShowModal(true);
  };

  const openEditModal = (faq) => {
    setEditingFaq(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category || 'General',
      sortOrder: faq.sortOrder,
      isActive: faq.isActive
    });
    setErrors({});
    setShowModal(true);
  };

  const openDeleteModal = (faq) => {
    setDeletingFaq(faq);
    setShowDeleteModal(true);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.question.trim()) newErrors.question = 'Question is required';
    if (!formData.answer.trim()) newErrors.answer = 'Answer is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (editingFaq) {
        await api.put(`/faq/admin/${editingFaq.id}`, formData);
      } else {
        await api.post('/faq/admin', formData);
      }
      setShowModal(false);
      fetchFaqs();
    } catch (err) {
      console.error('Failed to save FAQ:', err);
      setErrors({ submit: err.response?.data?.error || 'Failed to save FAQ' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingFaq) return;
    try {
      await api.delete(`/faq/admin/${deletingFaq.id}`);
      setShowDeleteModal(false);
      setDeletingFaq(null);
      fetchFaqs();
    } catch (err) {
      console.error('Failed to delete FAQ:', err);
    }
  };

  const handleToggleStatus = async (faq) => {
    try {
      await api.put(`/faq/admin/${faq.id}`, {
        question: faq.question,
        answer: faq.answer,
        category: faq.category,
        sortOrder: faq.sortOrder,
        isActive: !faq.isActive
      });
      fetchFaqs();
    } catch (err) {
      console.error('Failed to toggle FAQ status:', err);
    }
  };

  return (
    <AdminLayout>
      <div className="admin-faq-page">
        <div className="page-header">
          <h1>FAQ Management</h1>
          <button className="add-faq-btn" onClick={openAddModal}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add FAQ
          </button>
        </div>

        <div className="faq-table-container">
          {loading ? (
            <div className="loading-state">Loading FAQs...</div>
          ) : faqs.length === 0 ? (
            <div className="empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              <h3>No FAQs yet</h3>
              <p>Click "Add FAQ" to create your first frequently asked question.</p>
            </div>
          ) : (
            <table className="faq-table">
              <colgroup>
                <col style={{ width: '5%' }} />
                <col style={{ width: '25%' }} />
                <col style={{ width: '35%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Question</th>
                  <th>Answer</th>
                  <th className="th-center">Status</th>
                  <th className="th-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {faqs.map((faq, index) => (
                  <tr key={faq.id}>
                    <td className="td-num">{index + 1}</td>
                    <td className="td-question">{faq.question}</td>
                    <td className="td-answer">{faq.answer}</td>
                    <td className="td-status">
                      <div className="faq-toggle-row">
                        <div
                          className={`faq-toggle-switch ${faq.isActive ? 'active' : ''}`}
                          onClick={() => handleToggleStatus(faq)}
                          title={faq.isActive ? 'Click to deactivate' : 'Click to activate'}
                        />
                        <span className={`faq-toggle-label ${faq.isActive ? 'active' : 'inactive'}`}>
                          {faq.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td className="td-actions">
                      <button className="edit-btn" onClick={() => openEditModal(faq)}>Edit</button>
                      <button className="delete-btn" onClick={() => openDeleteModal(faq)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="faq-modal-overlay" onClick={() => setShowModal(false)}>
            <div className="faq-modal" onClick={(e) => e.stopPropagation()}>
              <h2>{editingFaq ? 'Edit FAQ' : 'Add New FAQ'}</h2>

              <div className="form-group">
                <label>Question <span className="required">*</span></label>
                <input
                  type="text"
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  placeholder="Enter the question"
                />
                {errors.question && <div className="form-error">{errors.question}</div>}
              </div>

              <div className="form-group">
                <label>Answer <span className="required">*</span></label>
                <textarea
                  value={formData.answer}
                  onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                  placeholder="Enter the answer"
                />
                {errors.answer && <div className="form-error">{errors.answer}</div>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    placeholder="e.g. General, Billing, Account"
                  />
                </div>
                <div className="form-group">
                  <label>Sort Order</label>
                  <input
                    type="number"
                    value={formData.sortOrder}
                    onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Status</label>
                <div className="toggle-group">
                  <div
                    className={`toggle-switch ${formData.isActive ? 'active' : ''}`}
                    onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                  />
                  <span>{formData.isActive ? 'Active' : 'Inactive'}</span>
                </div>
              </div>

              {errors.submit && <div className="form-error" style={{ marginBottom: 12 }}>{errors.submit}</div>}

              <div className="modal-actions">
                <button className="cancel-btn" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="save-btn" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : (editingFaq ? 'Update' : 'Create')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirm Modal */}
        {showDeleteModal && deletingFaq && (
          <div className="faq-modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="faq-modal" onClick={(e) => e.stopPropagation()} style={{ width: 420 }}>
              <div className="faq-delete-confirm">
                <h2>Delete FAQ</h2>
                <p>Are you sure you want to delete this FAQ?</p>
                <div className="delete-question">"{deletingFaq.question}"</div>
                <div className="modal-actions">
                  <button className="cancel-btn" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                  <button className="confirm-delete-btn" onClick={handleDelete}>Delete</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminFAQPage;
